import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '../../lib/utils';
import type {
  TooltipBodyProps,
  TooltipContentProps,
  TooltipProps,
  TooltipProviderProps,
  TooltipTriggerProps,
} from './types';

function TooltipProvider({
  delayDuration = 100,
  ...props
}: TooltipProviderProps) {
  return <TooltipPrimitive.Provider delayDuration={delayDuration} {...props} />;
}

function TooltipBody(props: TooltipBodyProps) {
  return <TooltipPrimitive.Root {...props} />;
}

const TooltipTrigger = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  TooltipTriggerProps
>(({ onClick, asChild = true, ...props }, ref) => {
  return (
    <TooltipPrimitive.Trigger
      ref={ref}
      asChild={asChild}
      onClick={(e) => {
        onClick?.(e); // no preventDefault/stopPropagation
      }}
      {...props}
    />
  );
});
TooltipTrigger.displayName = 'TooltipTrigger';

function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: TooltipContentProps) {
  const arrowBgColorClass =
    className?.match(/eye:bg-[^ ]+/)?.[0] || 'eye:bg-black';
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        sideOffset={sideOffset}
        className={cn(
          'eye:body-s eye:z-1500 eye:max-w-[220px] eye:min-w-[52px] eye:rounded-sm eye:bg-black',
          'eye:px-3xs eye:py-4xs eye:animate-in eye:fade-in-0 eye:zoom-in-95',
          'eye:wrap-break-word eye:data-[side=bottom]:slide-in-from-top-2',
          'eye:data-[side=left]:slide-in-from-right-2 eye:data-[side=right]:slide-in-from-left-2',
          'eye:data-[side=top]:slide-in-from-bottom-2 eye:data-[state=closed]:animate-out',
          'eye:data-[state=closed]:fade-out-0 eye:data-[state=closed]:zoom-out-95 eye:[&]:text-white',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            'eye:size-12 eye:translate-y-[calc(-50%-1px)] eye:rotate-45 eye:fill-none',
            arrowBgColorClass,
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

const Tooltip = React.forwardRef<
  React.ComponentRef<typeof TooltipPrimitive.Trigger>,
  TooltipProps
>(function Tooltip(
  {
    children,
    content,
    open,
    defaultOpen,
    onOpenChange,
    asChild = true,
    delayDuration,
    side,
    sideOffset,
    align,
    alignOffset,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
    ...props
  },
  ref,
) {
  const positionProps = {
    side,
    sideOffset,
    align,
    alignOffset,
    avoidCollisions,
    collisionBoundary,
    collisionPadding,
    sticky,
    hideWhenDetached,
    updatePositionStrategy,
  } satisfies Partial<TooltipContentProps>;

  if (!content) {
    return children;
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <TooltipBody
        open={open}
        defaultOpen={defaultOpen}
        onOpenChange={onOpenChange}
      >
        <TooltipTrigger ref={ref} asChild={asChild} {...props}>
          {children}
        </TooltipTrigger>
        <TooltipContent {...positionProps} {...props}>
          {content}
        </TooltipContent>
      </TooltipBody>
    </TooltipProvider>
  );
});

export {
  Tooltip,
  TooltipBody,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
};
